import type { z } from 'zod';

import { createAdminListSearchSchema } from '@/router/listSearch';
import type {
  InstallerJobHistoryItemDto,
  InstallerJobHistoryParams,
} from '@/ui/pages/Installer/installer.types';
import { collectSearchTerms } from '@/utils/listQueryParams';

/**
 * The installer's job history (HS-1641): URL state as every factory list keeps it — `page` and `q`.
 * No filter (sweep 2026-10-12): `GET /api/installer/me/jobs/history` takes no status parameter, and
 * no sort — the server orders by completion, newest first.
 */
export const jobHistorySearchSchema = createAdminListSearchSchema();

export type JobHistorySearch = z.infer<typeof jobHistorySearchSchema>;

export function jobHistoryQueryParams(search: JobHistorySearch): InstallerJobHistoryParams {
  const terms = collectSearchTerms([], search.q ?? '');
  return { page: search.page, pageSize: search.pageSize, ...(terms ? { search: terms } : {}) };
}

/** The text each column holds for a row — what the cards and client-side reads use. */
export const jobHistoryColumnText = {
  job: (row: InstallerJobHistoryItemDto) => row.number,
  institution: (row: InstallerJobHistoryItemDto) => row.institution.name,
  labs: (row: InstallerJobHistoryItemDto) => row.labs.map((lab) => lab.name).join(', '),
  devices: (row: InstallerJobHistoryItemDto) =>
    `${String(row.devices.installed)}/${String(row.devices.total)}`,
  status: (row: InstallerJobHistoryItemDto) => row.status,
  assigned: (row: InstallerJobHistoryItemDto) => row.assignedAt,
  completed: (row: InstallerJobHistoryItemDto) => row.completedAt,
} as const;

export type JobHistoryColumnId = keyof typeof jobHistoryColumnText;
